import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dynamicEndpointsApi, endpointsApi } from '@/services/api';
import type { AsyncEndpointResponse } from '@/types';

interface InvokeEndpointParams {
  /**
   * Owning account slug of the endpoint being invoked (task 385) — take it
   * from the endpoint row (`endpoint.accountSlug`), never from the caller's
   * token. Undefined/null falls back to the legacy unqualified URL.
   */
  accountSlug?: string | null;
  path: string;
  method: string;
  body?: unknown;
  /**
   * Deployment environment to exercise (task 760). Present only when the User
   * selected one on the Test surface; the call then routes through
   * `/api/{slug}/{env}/{path}` and hits exactly what that stage serves, rather
   * than the working state.
   */
  environment?: string | null;
}

// Hook for invoking an endpoint at /api/{accountSlug}/{environment?}/{path}
export const useInvokeEndpoint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountSlug, path, method, body, environment }: InvokeEndpointParams) => {
      const response = await dynamicEndpointsApi.invoke(
        accountSlug,
        path,
        method,
        body,
        environment
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate endpoint requests to refresh history
      queryClient.invalidateQueries({ queryKey: ['endpoint-requests'] });
    },
  });
};

// Hook for polling endpoint request status (for async responses)
export const useEndpointRequestStatus = (
  endpointId: string | undefined,
  requestId: string | undefined,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) => {
  return useQuery({
    queryKey: ['endpoint-request-status', endpointId, requestId],
    queryFn: async () => {
      if (!endpointId || !requestId) {
        throw new Error('Endpoint ID and Request ID are required');
      }
      const response = await endpointsApi.getRequest(endpointId, requestId);
      return response.data.data;
    },
    enabled: !!endpointId && !!requestId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
  });
};

// Hook for getting endpoint responses
export const useEndpointRequestResponses = (
  endpointId: string | undefined,
  requestId: string | undefined,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) => {
  return useQuery({
    queryKey: ['endpoint-responses', endpointId, requestId],
    queryFn: async () => {
      if (!endpointId || !requestId) {
        throw new Error('Endpoint ID and Request ID are required');
      }
      const response = await endpointsApi.getResponses(endpointId, requestId);
      return response.data;
    },
    enabled: !!endpointId && !!requestId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
  });
};

// Type guard for async response
export function isAsyncResponse(response: unknown): response is AsyncEndpointResponse {
  if (!response || typeof response !== 'object') return false;
  const r = response as Record<string, unknown>;
  if (!r.data || typeof r.data !== 'object') return false;
  const data = r.data as Record<string, unknown>;
  return data.status === 'queued' && typeof data.requestId === 'string';
}
